import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";

const initialState = {
  subscriptionData: {
    data: [],
  },
  status: "idle",
  error: null,
};

const { httpRequest } = Http();

/**
 * Async thunk for fetching subscriptions list.
 */
export const subscriptionList = createAsyncThunk(
  "listSubscriptions",
  async ({ navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: "admin/locations/subscriptions",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch subscriptions", {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for cancelling a subscription.
 */
export const cancelSubscription = createAsyncThunk(
  "cancelSubscription",
  async ({ id, cancelImmediately = false, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/locations/subscriptions/${id}/cancel`,
          body: { cancel_immediately: cancelImmediately },
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to cancel subscription", {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for resuming a subscription.
 */
export const resumeSubscription = createAsyncThunk(
  "resumeSubscription",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          url: `admin/locations/subscriptions/${id}/resume`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to resume subscription", {
        cause: err.cause,
      });
    }
  }
);

const manageSubscriptionSlice = createSlice({
  name: "manageSubscription",
  initialState,
  reducers: {
    showToast: (state, action) => {
      const { success, message } = action.payload;
      toastHandler(success, message);
    },
    resetSubscriptionState: (state) => {
      state.subscriptionData = { data: [] };
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            subscriptionList.pending,
            cancelSubscription.pending,
            resumeSubscription.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          state.error = null;
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(true);
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            subscriptionList.fulfilled,
            cancelSubscription.fulfilled,
            resumeSubscription.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";
          const actionType = action.type.split("/")[0];

          if (actionType === "listSubscriptions") {
            state.subscriptionData = {
              data:
                action.payload?.subscriptions?.data ||
                action.payload?.data ||
                [],
            };
          }

          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }

          if (["cancelSubscription", "resumeSubscription"].includes(actionType)) {
            const successMessages = {
              cancelSubscription: "Subscription cancelled successfully",
              resumeSubscription: "Subscription resumed successfully",
            };
            manageSubscriptionSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message || successMessages[actionType],
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            subscriptionList.rejected,
            cancelSubscription.rejected,
            resumeSubscription.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          state.error = action.payload;
          if (action.meta.arg?.setLoader) {
            action.meta.arg.setLoader(false);
          }
          manageSubscriptionSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export const { resetSubscriptionState } = manageSubscriptionSlice.actions;
export default manageSubscriptionSlice.reducer;
